import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getPlantillas,
  getCamposPlantilla,
  generarDesdePlantilla,
} from 'services/escritosJudicialesService';

const TIPO_INPUT = {
  DATE: 'date',
  NUMBER: 'number',
  TEXT: 'text',
};

// Lo que la casa le pasa a todo ComboBox dentro de una grilla (ver TaskCreateEditModal:351-362).
// Sin `width` no llena la columna, y sin el portal el desplegable queda cortado por el contenedor.
const PROPS_SELECTOR = {
  width: '100%',
  menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
  menuPosition: 'fixed',
};

// "Datos específicos" va siempre a dos columnas de media línea, igual que el <Col md={6}> del
// sistema externo. Sin excepciones por tipo de campo: con anchos distintos las filas quedaban
// desparejas y con huecos.
const ANCHO_ESPECIFICO = 'col-md-6';

// Los dos vocabularios conviven: las 18 heredadas usan @Rol / @Tribunal / @Caratulado y las 49 del
// estudio usan {{ROL_CAUSA}} / {{TRIBUNAL}} / {{CARATULADO}}. La sección 1 escribe en TODOS los
// alias de cada dato, para que la corrección del abogado llegue con el nombre que la plantilla
// espera. Una sola lista, usada también al cambiar de plantilla.
const IDENTIFICACION = [
  { clave: 'rol', etiqueta: 'cw_pl_rol', ancho: 'col-md-3', marcadores: ['Rol', 'ROL_CAUSA'] },
  { clave: 'tribunal', etiqueta: 'cw_pl_tribunal', ancho: 'col-md-4', marcadores: ['Tribunal', 'TRIBUNAL'] },
  { clave: 'caratulado', etiqueta: 'cw_pl_caratulado', ancho: 'col-md-5', marcadores: ['Caratulado', 'CARATULADO'] },
];

const MARCADORES_IDENTIFICACION = IDENTIFICACION.flatMap((c) => c.marcadores);

/**
 * Escritos desde plantilla: el camino sin IA.
 *
 * El formulario muestra TODOS los campos, en las mismas tres secciones que el del sistema externo.
 * La diferencia es la mejora: allá el abogado tipea rol, tribunal y caratulado en cada escrito;
 * acá llegan de la causa, marcados "de la causa" y editables — si el dato está mal, lo corrige y
 * lo suyo gana.
 */
const PlantillasPanel = ({ DesignSystemComponents, caseData, t }) => {
  const { Button, Loader, Alert, Input, ComboBox, Dropdown } =
    DesignSystemComponents || {};

  // El ComboBox es buscable (se escribe dentro para filtrar); el Dropdown no. Misma caída que
  // usa el resto del proyecto por si el sistema de diseño no lo trae.
  const Selector = ComboBox || Dropdown;

  // Igual que CaseDetails: la causa puede venir del contexto o de la ruta. Si se entra por URL
  // directa, `caseData` llega sin id y sin este respaldo nada se autocompletaría.
  const { id: idRuta } = useParams();
  const causaId = caseData?.id ?? (idRuta ? Number(idRuta) : null);

  const [plantillas, setPlantillas] = useState([]);
  const [seleccionada, setSeleccionada] = useState('');
  const [grupo, setGrupo] = useState('');
  const [campos, setCampos] = useState([]);
  const [cargandoCampos, setCargandoCampos] = useState(false);
  const [valores, setValores] = useState({});
  const [generado, setGenerado] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState(null);

  const abortLista = useRef(null);
  const abortCampos = useRef(null);
  const abortGenerar = useRef(null);

  useEffect(
    () => () => {
      abortLista.current?.abort();
      abortCampos.current?.abort();
      abortGenerar.current?.abort();
    },
    []
  );

  useEffect(() => {
    abortLista.current?.abort();
    const controller = new AbortController();
    abortLista.current = controller;

    setCargando(true);
    setError(null);
    getPlantillas(null, controller.signal)
      .then((data) => setPlantillas(data))
      .catch((err) => {
        if (err.name === 'AbortError' || err.name === 'CanceledError') return;
        setError(err.customMessage || err.message);
      })
      .finally(() => setCargando(false));
  }, []);

  const elegir = useCallback(
    async (plantillaId) => {
      setSeleccionada(plantillaId);
      setGenerado(null);
      setCampos([]);
      // Se marca antes de vaciar los campos: con la lista vacía, la validación de obligatorios no
      // ve nada que falte y el botón queda habilitado. Apretarlo en esa ventana genera el escrito
      // sin los datos que la plantilla pide, y sale con los marcadores literales adentro.
      setCargandoCampos(Boolean(plantillaId));
      // La identificación de la causa está ARRIBA del selector: si el abogado corrigió el rol y
      // después elige la plantilla, su corrección no se pierde.
      setValores((previos) =>
        Object.fromEntries(
          MARCADORES_IDENTIFICACION.filter((m) => previos[m] !== undefined).map((m) => [
            m,
            previos[m],
          ])
        )
      );
      if (!plantillaId) return;

      abortCampos.current?.abort();
      const controller = new AbortController();
      abortCampos.current = controller;

      try {
        const data = await getCamposPlantilla(
          plantillaId,
          causaId,
          controller.signal
        );
        setCampos(data);
        // Los que vienen de la causa llegan con valor: se precargan en el formulario, pero sin
        // pisar lo que el abogado ya haya escrito.
        setValores((previos) => ({
          ...Object.fromEntries(
            data.filter((c) => c.valor).map((c) => [c.marcador, c.valor])
          ),
          ...Object.fromEntries(
            Object.entries(previos).filter(([, v]) => String(v ?? '').trim())
          ),
        }));
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') return;
        setError(err.customMessage || err.message);
      } finally {
        setCargandoCampos(false);
      }
    },
    [causaId]
  );

  // La sección 1 es un bloque FIJO de tres campos, igual que en el sistema externo: está siempre,
  // antes de elegir la plantilla, y no depende de ella. Allá el abogado los tipea en cada escrito;
  // acá salen de la causa que el detalle ya cargó.
  const identificacion = useMemo(() => {
    const deLaCausa = {
      rol: caseData?.code,
      tribunal: caseData?.court,
      caratulado: caseData?.title,
    };
    return IDENTIFICACION.map((c) => ({ ...c, valor: deLaCausa[c.clave] }));
  }, [caseData?.code, caseData?.court, caseData?.title]);

  /** Lo que se muestra en el campo: lo que el abogado escribió, o el dato de la causa. */
  const valorIdentificacion = useCallback(
    (campo) => {
      const escrito = campo.marcadores
        .map((m) => valores[m])
        .find((v) => v !== undefined && v !== null);
      return escrito ?? campo.valor ?? '';
    },
    [valores]
  );

  const escribirIdentificacion = useCallback((campo, texto) => {
    setValores((previos) => {
      const siguiente = { ...previos };
      campo.marcadores.forEach((m) => {
        siguiente[m] = texto;
      });
      return siguiente;
    });
  }, []);

  // Todo lo que el abogado completa va en la sección 3, con el mismo criterio del externo: los
  // datos del estudio no son una sección aparte allá —están escritos a mano dentro del archivo de
  // reglas— y separarlos generaba una sección que el usuario no reconoce.
  const camposEspecificos = useMemo(
    () => campos.filter((c) => c.origenValor !== 'CASE'),
    [campos]
  );

  const plantillaActual = useMemo(
    () => plantillas.find((p) => String(p.id) === String(seleccionada)),
    [plantillas, seleccionada]
  );

  // `grupo` es el conjunto abierto (LibraryGroup) y `categoria` el cerrado. Hoy las 18 heredadas
  // no tienen grupo, así que caen en su categoría: el árbol funciona igual y respeta el grupo en
  // cuanto alguien lo asigne. Mismo criterio del sistema externo, que también cae a un cajón.
  const grupoDe = useCallback(
    (p) => p.grupo || p.categoria || t('cw_pl_grupoPorDefecto'),
    [t]
  );


  // `opciones` viene como JSON desde la base. Si algún día llega mal formado, el campo queda sin
  // opciones en vez de tumbar la pestaña entera.
  const opcionesDe = useCallback((campo) => {
    try {
      return JSON.parse(campo.opciones || '[]').map((o) => ({ label: o, value: o }));
    } catch {
      return [];
    }
  }, []);

  const opcionesGrupo = useMemo(
    () =>
      [...new Set(plantillas.map(grupoDe))]
        .sort((a, b) => a.localeCompare(b, 'es'))
        .map((g) => ({ label: g, value: g })),
    [plantillas, grupoDe]
  );

  // Sin grupo elegido se ofrecen TODAS: el ComboBox ya filtra al escribir, así que cuando el
  // abogado sabe el nombre no necesita pasar por el grupo. Eso reemplaza al buscador aparte que
  // tiene el sistema externo.
  const opcionesPlantilla = useMemo(
    () =>
      plantillas
        .filter((p) => !grupo || grupoDe(p) === grupo)
        .map((p) => ({
          label: grupo ? p.nombre : `[${grupoDe(p)}] ${p.nombre}`,
          value: String(p.id),
        })),
    [plantillas, grupo, grupoDe]
  );

  // Solo bloquea por los campos que el abogado PUEDE llenar. Los de la causa no se dibujan, así
  // que exigirlos dejaba el botón deshabilitado para siempre y sin explicación: pasaba con
  // RUT_PATROCINANTE cuando el sponsor de la causa no tiene el RUT cargado.
  //
  // Un campo de la causa que no se pueda resolver tiene que salir por el otro camino: se genera,
  // el sustituidor lo reporta y el aviso rojo dice cuál falta. Eso el abogado lo entiende; un
  // botón gris no.
  const faltanObligatorios = useMemo(
    () =>
      camposEspecificos.some(
        (c) => c.obligatorio && !String(valores[c.marcador] || '').trim()
      ),
    [camposEspecificos, valores]
  );

  const esDocx = generado?.formato === 'DOCX';

  // Recibe el escrito por parámetro y no del estado, porque se llama desde `generar` con el dato
  // recién traído: en ese momento `generado` todavía no está seteado.
  const descargar = useCallback((escrito) => {
    const doc = escrito ?? generado;
    if (!doc) return;

    let blob;
    let nombre;

    if (doc.formato === 'DOCX') {
      if (!doc.archivoBase64) return;
      // El backend manda el Word en base64. atob da una cadena de bytes: hay que pasarla a
      // Uint8Array o el .docx llega corrupto (un zip no sobrevive a una conversión de texto).
      const bytes = Uint8Array.from(atob(doc.archivoBase64), (c) => c.charCodeAt(0));
      blob = new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      nombre = doc.nombreArchivo || `escrito-${doc.documentoId}.docx`;
    } else {
      if (!doc.contenido) return;
      blob = new Blob([doc.contenido], { type: 'application/msword;charset=utf-8' });
      nombre = `escrito-${doc.documentoId}.doc`;
    }

    const url = URL.createObjectURL(blob);
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = nombre;
    document.body.appendChild(enlace);
    enlace.click();
    document.body.removeChild(enlace);
    URL.revokeObjectURL(url);
  }, [generado]);

  const generar = useCallback(async () => {
    if (!seleccionada || !causaId) return;
    abortGenerar.current?.abort();
    const controller = new AbortController();
    abortGenerar.current = controller;

    setGenerando(true);
    setError(null);
    try {
      const data = await generarDesdePlantilla(
        causaId,
        Number(seleccionada),
        valores,
        controller.signal
      );
      setGenerado(data);

      // Baja el archivo solo. El segundo clic existía nada más para poder mirar los marcadores
      // sin resolver ANTES de tener el documento en la mano; si no hay ninguno, no aporta.
      //
      // Con marcadores sin resolver NO se descarga: el escrito tiene huecos y va a un tribunal.
      // Ahí queda el aviso y un botón para bajarlo igual, que es una decisión del abogado.
      if (!data.marcadoresSinResolver?.length) descargar(data);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(err.customMessage || err.response?.data?.message || err.message);
    } finally {
      setGenerando(false);
    }
  }, [seleccionada, causaId, valores, descargar]);

  return (
    <div>
      <p className='font-light mb-2'>{t('cw_pl_paso1')}</p>

      {/* Grilla como en TaskCreateEditModal: el ancho de cada campo sigue a su contenido. Un rol
          de 12 caracteres en un input de 1.200 px se ve roto. */}
      <div className='row g-3'>
        {identificacion.map((campo) => (
          <div className={`col-12 ${campo.ancho}`} key={campo.clave}>
            <Input
              label={t(campo.etiqueta)}
              type='text'
              value={valorIdentificacion(campo)}
              onChange={(e) => escribirIdentificacion(campo, e.target.value)}
            />
          </div>
        ))}
      </div>

      {/* Separador entre secciones. `line` es la clase de la casa (ver DailyState, Collection). */}
      <hr className='line mt-4 mb-3' />

      <p className='font-light mb-2'>{t('cw_pl_paso2')}</p>

      {cargando && <Loader />}

      <div className='row g-3'>
        <div className='col-12 col-md-4'>
          <Selector
            label={t('cw_pl_grupo')}
            placeholder={t('cw_pl_grupo_ph')}
            options={opcionesGrupo}
            value={grupo ? { label: grupo, value: grupo } : null}
            onChange={(elegido) => {
              setGrupo(elegido?.value ?? '');
              elegir('');
            }}
            isClearable
            {...PROPS_SELECTOR}
          />
        </div>

        <div className='col-12 col-md-8'>
          <Selector
            label={t('cw_pl_tipo')}
            placeholder={t('cw_pl_tipo_ph')}
            options={opcionesPlantilla}
            value={opcionesPlantilla.find((o) => o.value === String(seleccionada)) ?? null}
            onChange={(elegido) => elegir(elegido?.value ?? '')}
            isClearable
            {...PROPS_SELECTOR}
          />
        </div>
      </div>

      {!cargando && plantillas.length === 0 && (
        <p className='caption color-gray-600 mb-0 mt-2'>{t('cw_pl_sinPlantillas')}</p>
      )}

      {seleccionada && (
        <>
          <hr className='line mt-4 mb-3' />

          <p className='font-light mb-2'>
            {t('cw_pl_paso3', { nombre: plantillaActual?.nombre || '' })}
          </p>

          <div className='row g-3'>
            {camposEspecificos.map((campo) => (
              <div className={`col-12 ${ANCHO_ESPECIFICO}`} key={campo.marcador}>
                {campo.tipo === 'TEXTAREA' ? (
                  <>
                    <label className='form-label'>{campo.etiqueta}</label>
                    <textarea
                      className='form-control'
                      rows={4}
                      value={valores[campo.marcador] || ''}
                      onChange={(e) =>
                        setValores((p) => ({ ...p, [campo.marcador]: e.target.value }))
                      }
                    />
                  </>
                ) : campo.tipo === 'SELECT' ? (
                  <Selector
                    label={campo.etiqueta}
                    required={campo.obligatorio}
                    placeholder={t('cw_pl_selecciona')}
                    options={opcionesDe(campo)}
                    value={
                      opcionesDe(campo).find(
                        (o) => o.value === (valores[campo.marcador] || '')
                      ) ?? null
                    }
                    onChange={(elegido) =>
                      setValores((p) => ({ ...p, [campo.marcador]: elegido?.value ?? '' }))
                    }
                    isClearable
                    {...PROPS_SELECTOR}
                  />
                ) : (
                  <Input
                    label={campo.etiqueta}
                    required={campo.obligatorio}
                    type={TIPO_INPUT[campo.tipo] || 'text'}
                    value={valores[campo.marcador] || ''}
                    onChange={(e) =>
                      setValores((p) => ({ ...p, [campo.marcador]: e.target.value }))
                    }
                  />
                )}
              </div>
            ))}
          </div>

          <div className='mt-4 d-flex justify-content-center'>
            {/* El texto va en `label`: el Button del sistema de diseño NO usa hijos —pasarlo como
                hijo dibuja un botón vacío—, y sin `variant`/`size` cae en un default que no es el
                de la casa. El patrón vivo es label + size='sm' + variant='primary'. */}
            <Button
              label={generando ? t('cw_pl_generando') : t('cw_pl_generar')}
              size='sm'
              variant='primary'
              onClick={generar}
              disabled={generando || cargandoCampos || faltanObligatorios || !causaId}
            />
          </div>
        </>
      )}

      {error && (
        <div className='mt-3'>
          <Alert className='alerta-en-linea' variant='danger'>{error}</Alert>
        </div>
      )}

      {generado && (
        <div>
          <hr className='line mt-4 mb-3' />

          {generado.marcadoresSinResolver?.length > 0 ? (
            <>
              <Alert className='alerta-en-linea' variant='danger'>
                {t('cw_pl_sinResolver', {
                  lista: generado.marcadoresSinResolver.join(', '),
                })}{' '}
                <b>{t('cw_pl_sinResolver_warn')}</b>.
              </Alert>

              {/* No se descargó solo, a propósito. Bajarlo con huecos es decisión del abogado. */}
              <div className='d-flex justify-content-center'>
                <Button
                  label={t('cw_pl_descargarIgual')}
                  size='sm'
                  variant='secondary'
                  onClick={() => descargar()}
                />
              </div>
            </>
          ) : (
            <p className='caption color-gray-600 mb-0'>
              {generado.nombreArchivo
                ? t('cw_pl_descargado', { nombre: generado.nombreArchivo })
                : t('cw_pl_descargadoSinNombre')}
            </p>
          )}

          {/* Un .docx no se puede previsualizar en el navegador: hay que abrirlo en Word. Las
              heredadas son HTML y sí se ven. */}
          {!esDocx && generado.contenido && (
            <div
              className='border rounded p-3 mt-3'
              style={{ maxHeight: '400px', overflowY: 'auto' }}
              dangerouslySetInnerHTML={{ __html: generado.contenido }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PlantillasPanel;
